import Seo from "../components/Seo.tsx";
import Divider from "../components/ornaments/Divider.tsx";
import Feather from "../components/ornaments/Feather.tsx";
import { ButtonLink } from "../components/ui/Button.tsx";
import CandleCard from "../components/candle/CandleCard.tsx";
import { useFeaturedProducts } from "../lib/products-context.tsx";

const trustPills = [
  "100% Vegan Wax",
  "Eco Packaging",
  "Clean Burn",
];

const editorial = [
  {
    label: "Scent Stories",
    title: "Every flame tells a story",
    body: "Our fragrances are built around emotional memory — each blend designed to take you somewhere meaningful the moment you strike the match.",
    cta: "Explore scents",
    to: "/collection",
  },
  {
    label: "Sustainability",
    title: "Zero compromise, zero toxins",
    body: "Plant-based vegan wax, recyclable vessels, minimal packaging. Beautiful things shouldn't cost the earth — literally.",
    cta: "Our values",
    to: "/craft",
  },
  {
    label: "A Candle of Your Own",
    title: "Composed for one",
    body: "Tell us your name and the day you were born. We'll compose a candle drawn from your sign — a private fragrance, a personal reading, a label of your own.",
    cta: "Compose yours",
    to: "/create",
  },
];

export default function Home() {
  const featured = useFeaturedProducts();

  return (
    <div className="mx-auto max-w-6xl px-6 md:px-10">
      <Seo
        title="BeAdoré Candles — Hand-Poured Luxury Candles"
        description="Hand-poured luxury candles crafted from 100% vegan wax. Explore our signature collection or compose a personal horoscope candle drawn from your zodiac sign."
        path="/"
      />
      <section className="relative mt-12 mb-24 overflow-hidden md:mt-16">
        <div className="marble-panel relative px-6 py-24 md:px-16 md:py-32">
          <Feather
            className="pointer-events-none absolute top-1/2 left-1/2 h-[680px] w-auto -translate-x-1/2 -translate-y-1/2 opacity-[0.08]"
            style={{
              animation: "var(--animate-fade-in)",
              animationDelay: "200ms",
            }}
          />
          <Feather
            className="pointer-events-none absolute top-10 right-10 hidden h-40 w-auto rotate-[18deg] opacity-70 md:block"
            style={{
              animation: "var(--animate-fade-up)",
              animationDelay: "500ms",
            }}
          />
          <Feather
            className="pointer-events-none absolute bottom-10 left-10 hidden h-32 w-auto -rotate-[24deg] opacity-60 md:block"
            style={{
              animation: "var(--animate-fade-up)",
              animationDelay: "650ms",
            }}
          />

          <div className="relative flex flex-col items-center text-center">
            <span
              className="eyebrow"
              style={{ animation: "var(--animate-fade-up)" }}
            >
              Hand-Poured · Small Batch
            </span>
            <h1
              className="mt-6 max-w-3xl font-serif text-5xl leading-[1.05] text-[var(--color-emerald-deep)] md:text-7xl"
              style={{
                animation: "var(--animate-fade-up)",
                animationDelay: "120ms",
              }}
            >
              Candles crafted to evoke your{" "}
              <em className="text-gold-leaf font-serif italic">memories.</em>
            </h1>
            <p
              className="mt-8 max-w-xl text-base leading-relaxed text-[var(--color-charcoal)]/75"
              style={{
                animation: "var(--animate-fade-up)",
                animationDelay: "260ms",
              }}
            >
              All-natural vegan wax candles poured with intention. Each scent
              is a doorway to a moment worth remembering.
            </p>
            <div
              className="mt-12 flex flex-col gap-4 sm:flex-row"
              style={{
                animation: "var(--animate-fade-up)",
                animationDelay: "420ms",
              }}
            >
              <ButtonLink to="/collection">Shop the Collection</ButtonLink>
              <ButtonLink to="/create" variant="ghost">
                Compose Your Own
              </ButtonLink>
            </div>

            <div
              className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3"
              style={{
                animation: "var(--animate-fade-up)",
                animationDelay: "560ms",
              }}
            >
              {trustPills.map((pill) => (
                <span
                  key={pill}
                  className="flex items-center gap-2 text-[0.7rem] font-light uppercase tracking-[0.14em] text-[var(--color-stone)]"
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-sage)]" />
                  {pill}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="mb-32">
        <div className="mb-12 flex flex-col items-center text-center">
          <span className="eyebrow">The Collection</span>
          <h2 className="mt-4 font-serif text-4xl text-[var(--color-emerald-deep)] md:text-5xl">
            Find your <em className="text-[var(--color-amber)]">scent</em>
          </h2>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {featured.map((p) => (
            <CandleCard key={p.id} product={p} />
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          <ButtonLink to="/collection" variant="ghost">
            View All Candles
          </ButtonLink>
        </div>
      </section>

      <Divider className="my-16" />

      <section className="mb-32 grid gap-px border border-[var(--color-rule)]/60 bg-[var(--color-rule)]/40 md:grid-cols-3">
        {editorial.map((item, i) => (
          <article
            key={item.title}
            className="group flex min-h-[340px] flex-col justify-between bg-[var(--color-warm-white)]/80 p-10 transition-colors duration-500 hover:bg-[var(--color-warm-white)]"
          >
            <div>
              <p className="text-[0.6rem] font-medium uppercase tracking-[0.18em] text-[var(--color-sage)]">
                {item.label}
              </p>
              <h3 className="mt-3 font-serif text-3xl leading-tight text-[var(--color-emerald-deep)]">
                {item.title}
              </h3>
              <p className="mt-5 max-w-xs text-sm leading-[1.7] font-light text-[var(--color-stone)]">
                {item.body}
              </p>
            </div>
            <a
              href={item.to}
              className="mt-8 flex items-center gap-2 text-[0.7rem] font-medium uppercase tracking-[0.14em] text-[var(--color-emerald-deep)] transition-all duration-300 group-hover:gap-4 group-hover:text-[var(--color-amber)]"
            >
              {item.cta} <span aria-hidden>→</span>
            </a>
            <span className="pointer-events-none absolute right-8 bottom-6 font-serif text-7xl text-[var(--color-rule)]/50">
              0{i + 1}
            </span>
          </article>
        ))}
      </section>
    </div>
  );
}
